import type { ReactElement } from "react"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import SliderTemplate from "@onestore/hel/dist/components/templates/SliderTemplate"
import _t from "@core/i18n"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import CtaElement from "@gatsby-plugin-generic-page/components/CtaElement"
import Header from "@gatsby-plugin-generic-page/components/Header"
import Media from "@gatsby-plugin-generic-page/components/Media"
import type { SliderSectionFlatData } from "@gatsby-plugin-generic-page/fragments/sliderSection"
import getLinkProps from "@gatsby-plugin-generic-page/helpers/getLinkProps"
import { usePriceTypeContext } from "~/context/PriceTypeContext"

function SliderSection({
  header,
  slides,
  hasArrowsOnMobile,
  ratio,
  ctaElements,
  sectionSettings,
}: SliderSectionFlatData): ReactElement<SliderSectionFlatData> {
  const { currentPriceType } = usePriceTypeContext()

  const sliderItems = slides.map((slide) => {
    const { responsiveImages, title, text, link } = slide

    const linkElement = link && getLinkProps(link, currentPriceType)

    return {
      image: (
        <Media media={responsiveImages} className="h-full absolute inset-0" />
      ),
      title,
      text,
      link: linkElement,
    }
  })

  const slideTitle = {
    dot: _t("slider.dot"),
    active: _t("slider.active"),
    next: _t("slider.next"),
    previous: _t("slider.previous"),
    play: _t("slider.play"),
    pause: _t("slider.pause"),
  }

  return (
    <BaseSection {...sectionSettings} hasOverflowHidden>
      <BaseContainer {...sectionSettings?.containerSettings}>
        {header ? (
          <div className="mb-8">
            <AlignContentOrg horizontalAlign="center">
              <Header {...header} />
            </AlignContentOrg>
          </div>
        ) : null}

        <BreakpointVisibilityOrg from="small" to="medium">
          <SliderTemplate
            ratio={ratio || "default"}
            hasArrowsOnMobile={hasArrowsOnMobile}
            slides={sliderItems}
            slideTitle={slideTitle}
          />
        </BreakpointVisibilityOrg>

        <BreakpointVisibilityOrg from="medium">
          <SliderTemplate
            ratio={ratio || "default"}
            slides={sliderItems}
            slideTitle={slideTitle}
          />
        </BreakpointVisibilityOrg>

        {ctaElements ? (
          <div className="mt-8">
            <AlignContentOrg horizontalAlign="center">
              <CtaElement ctaElement={ctaElements} />
            </AlignContentOrg>
          </div>
        ) : null}
      </BaseContainer>
    </BaseSection>
  )
}

export default SliderSection
